// Class-based error hierarchy for the expression engine.
//
// Pattern mirrors `packages/plugin-chat/src/lib/tool/errors/`: one abstract
// base, intermediate abstracts by failure category, concrete leaves with
// structured fields and a `suggestions()` recovery method. Adapted for two
// engine-specific concerns absent from tool errors:
//
// - **Source positions.** Every leaf carries a `pos: SourcePos` because
//   parse and eval failures both originate at a specific point in the
//   source (or `NO_POS` for direct JS-object input). `pos` is owned by the
//   base class — concrete leaves accept it as the trailing constructor arg
//   and pass it through `super(message, pos)`.
// - **Aggregation.** `Promise.allSettled` over sibling args can produce
//   multiple failures from a single op call. `AggregateExprError`
//   wraps them; callers flatten on demand via `flatten()`.

import type { z } from 'zod';

import { NO_POS, type SourcePos } from './ast.ts';
import { relaxSymbols } from './schema/sym-codec.ts';

// Base.

/**
 * Abstract base for every error thrown by the expression engine. Subclasses
 * are concrete: instantiate them directly, throw, and catch via `instanceof`.
 *
 * Recovery hints live in `suggestions()` — a `readonly string[]` of
 * imperative sentences aimed at both humans and LLMs. The default is empty.
 * Subclasses with a single fixed hint (or one templated from constructor
 * fields) just set `staticHint`; the base reads it. Subclasses that compute
 * hints dynamically (`UnknownNameError`, `InvalidTokenError`) override
 * `suggestions()` directly.
 */
export abstract class ExprError extends Error {
  /**
   * Set by subclasses that have one fixed recovery hint. The base
   * `suggestions()` returns `[staticHint]` when set, `[]` otherwise.
   * Excluded from `details()` via `RESERVED_DETAIL_KEYS` — it's an
   * internal mechanism, not a structural field worth surfacing.
   */
  protected readonly staticHint?: string;

  constructor(
    message: string,
    readonly pos: SourcePos,
  ) {
    super(message);
    // Restore the prototype chain across module boundaries (subclass
    // `instanceof` checks would otherwise break after transpilation).
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = new.target.name;
  }

  /** Recovery suggestions for callers / LLMs. */
  suggestions(): readonly string[] {
    return this.staticHint === undefined ? [] : [this.staticHint];
  }

  /** `line:col: message` — compact rendering convenient for logs. */
  format(): string {
    return `${this.pos.line}:${this.pos.col}: ${this.message}`;
  }

  /**
   * Structured view of the error for logging or wire serialisation.
   * Default impl reflects own enumerable properties minus the Error-base
   * fields. Subclasses override when they need a custom shape (e.g.
   * `AggregateExprError` flattens its nested errors).
   */
  details(): Readonly<Record<string, unknown>> {
    const out: Record<string, unknown> = {};
    for (const key of Object.keys(this)) {
      if (RESERVED_DETAIL_KEYS.has(key)) {
        continue;
      }
      out[key] = (this as unknown as Record<string, unknown>)[key];
    }
    return out;
  }
}

const RESERVED_DETAIL_KEYS = new Set(['message', 'stack', 'cause', 'staticHint']);

/**
 * Shared "did-you-mean" suggestion shape. Returns either an empty-state
 * hint (when no names are in scope) or a single sentence listing the
 * available names. Used by error classes that carry an `available*` list.
 */
const availableNamesSuggestion = (
  emptyHint: string,
  listPrefix: string,
  names: readonly string[],
): readonly string[] => (names.length === 0 ? [emptyHint] : [`${listPrefix}: ${names.join(', ')}.`]);

// Aggregation.

/**
 * Container error wrapping multiple sibling failures. Thrown when
 * `Promise.allSettled` over arg evaluations finds 2+ branches errored.
 * The tree structure is preserved as-given; callers call `flatten()` to
 * materialise the leaf list (which transparently descends into nested
 * aggregates — spec §9.3).
 */
export class AggregateExprError extends ExprError {
  constructor(readonly errors: readonly ExprError[]) {
    super(`${errors.length} errors during evaluation`, errors[0]?.pos ?? NO_POS);
  }

  /** Walk the tree, returning a flat list of leaf errors (non-aggregate). */
  flatten(): readonly ExprError[] {
    return this.errors.flatMap((e) => (e instanceof AggregateExprError ? e.flatten() : [e]));
  }

  override details(): Readonly<Record<string, unknown>> {
    return {
      pos: this.pos,
      errors: this.errors.map((e) => ({ kind: e.constructor.name, ...e.details() })),
    };
  }
}

// Parser errors.

/** Abstract intermediate for every parse-time error. */
export abstract class ParseError extends ExprError {}

export class UnclosedListError extends ParseError {
  constructor(pos: SourcePos) {
    super('unclosed list — expected )', pos);
  }
}

export class UnclosedVectorError extends ParseError {
  constructor(pos: SourcePos) {
    super('unclosed vector — expected ]', pos);
  }
}

export class StrayRparenError extends ParseError {
  constructor(pos: SourcePos) {
    super('unmatched closing )', pos);
  }
}

export class StrayRbracketError extends ParseError {
  constructor(pos: SourcePos) {
    super('unmatched closing ]', pos);
  }
}

export class EmptyListError extends ParseError {
  protected override readonly staticHint = 'Lists are op calls; write (op-name ...args).';
  constructor(pos: SourcePos) {
    super('empty list — every list must have an op symbol as its head', pos);
  }
}

export class ListHeadNotSymbolError extends ParseError {
  protected override readonly staticHint = 'Lists are op calls; the first element must be a symbol naming the op.';
  constructor(
    readonly headDescription: string,
    pos: SourcePos,
  ) {
    super(`list head must be an op symbol, got ${headDescription}`, pos);
  }
}

export class BareKeywordError extends ParseError {
  protected override readonly staticHint = 'Keywords are only valid as kwarg tags inside an op call: (op :kw value).';
  constructor(
    readonly keyword: string,
    pos: SourcePos,
  ) {
    super(`bare keyword :${keyword} is not a value`, pos);
  }
}

export class KwargNoValueError extends ParseError {
  protected override readonly staticHint: string;
  constructor(
    readonly keyword: string,
    pos: SourcePos,
  ) {
    super(`keyword :${keyword} is missing its value`, pos);
    this.staticHint = `Provide a value after :${keyword}, e.g. :${keyword} <value>.`;
  }
}

export class ReservedWordError extends ParseError {
  constructor(
    readonly word: string,
    pos: SourcePos,
  ) {
    super(`reserved word "${word}" cannot be used as a keyword name`, pos);
  }
}

export class UnterminatedStringError extends ParseError {
  constructor(pos: SourcePos) {
    super('unterminated string literal', pos);
  }
}

export class UnknownEscapeError extends ParseError {
  protected override readonly staticHint = 'Valid escapes: \\" \\\\ \\n \\t \\r \\uXXXX';
  constructor(
    readonly escapeSequence: string,
    pos: SourcePos,
  ) {
    super(`unknown escape sequence \\${escapeSequence}`, pos);
  }
}

export class InvalidNumberError extends ParseError {
  protected override readonly staticHint =
    'Numbers are: integers, decimals, scientific (1.5e6), with optional `_` separators (1_000_000) and optional leading `-`.';
  constructor(
    readonly literal: string,
    pos: SourcePos,
  ) {
    super(`invalid number literal "${literal}"`, pos);
  }
}

export class InvalidTokenError extends ParseError {
  constructor(
    readonly token: string,
    pos: SourcePos,
  ) {
    super(`invalid token "${token}"`, pos);
  }
  override suggestions(): readonly string[] {
    if (this.token.includes('.')) {
      return ['Dots are not allowed in symbols. For cross-dataset column references, use (col <dataset> <column>).'];
    }
    return [];
  }
}

export class EmptyKeywordError extends ParseError {
  protected override readonly staticHint = 'Use a name like :where, :group-by, :as.';
  constructor(pos: SourcePos) {
    super('keyword must have an identifier after the colon', pos);
  }
}

export class UnexpectedEofError extends ParseError {
  constructor(
    readonly context: string,
    pos: SourcePos,
  ) {
    super(context, pos);
  }
}

// Evaluator errors.

/**
 * Abstract intermediate for every evaluation-time error. Named
 * `EvaluationError` (not `EvalError`) to avoid shadowing the JS global.
 */
export abstract class EvaluationError extends ExprError {}

/**
 * Abstract base for the three "name not in scope" errors —
 * `UnknownOpError`, `UnboundSymbolError`, `UnknownVerbError`. Each encodes
 * the same shape: a `name` was referenced that isn't in this scope's name
 * table. Subclasses supply the message template plus the empty-state hint
 * and list prefix used to render `availableNamesSuggestion`. Subclasses
 * stay distinct so `instanceof` discrimination tells "op missing" from
 * "binding missing" from "verb missing".
 */
export abstract class UnknownNameError extends EvaluationError {
  readonly #emptyHint: string;
  readonly #listPrefix: string;
  constructor(
    override readonly name: string,
    readonly available: readonly string[],
    pos: SourcePos,
    message: string,
    emptyHint: string,
    listPrefix: string,
  ) {
    super(message, pos);
    this.#emptyHint = emptyHint;
    this.#listPrefix = listPrefix;
  }
  override suggestions(): readonly string[] {
    return availableNamesSuggestion(this.#emptyHint, this.#listPrefix, this.available);
  }
}

export class UnknownOpError extends UnknownNameError {
  constructor(name: string, available: readonly string[], pos: SourcePos) {
    super(name, available, pos, `unknown op: ${name}`, 'No ops are registered.', 'Available ops');
  }
}

export class UnboundSymbolError extends UnknownNameError {
  constructor(name: string, available: readonly string[], pos: SourcePos) {
    super(
      name,
      available,
      pos,
      `unbound symbol: ${name}`,
      'No bindings are in scope. Introduce one via (dataset name expr).',
      'Available names in scope',
    );
  }
}

/**
 * Map a Zod issue path to a human-facing argument label for `ShapeError`.
 * Normalised op calls have shape `{ name, params: [...], <kwarg>: ... }`,
 * so `path[0]` is `'params'` for a positional (label `#<index>`), `'name'`
 * for the op-name literal (no arg label — the issue is about the call
 * itself), and any other key for a keyword argument (label `:<key>`).
 * Single source of truth for the issue-path → arg-label mapping, shared by
 * every site that turns a Zod issue into a `ShapeError`.
 */
export const argRefFromZodPath = (path: readonly PropertyKey[]): string | undefined => {
  const head = path[0];
  if (head === undefined || head === 'name') {
    return undefined;
  }
  if (head === 'params') {
    return `#${String(path[1] ?? 0)}`;
  }
  return `:${String(head)}`;
};
/**
 * The raw Zod-v4 issue fields {@link engineZodErrorMap} reads. Zod's
 * internal `$ZodRawIssue` is a `code`-discriminated union; this is the
 * narrow structural view the formatter needs — a deliberate boundary
 * refinement (a Zod issue object is untyped input from our perspective).
 */
type ZodRawIssueView = {
  readonly code: string;
  readonly path?: readonly PropertyKey[];
  readonly origin?: string;
  readonly minimum?: number;
  readonly maximum?: number;
  readonly inclusive?: boolean;
  readonly input?: unknown;
  readonly keys?: readonly string[];
  readonly errors?: readonly (readonly { readonly expected?: string; readonly values?: readonly unknown[] }[])[];
};

/**
 * Engine-wide Zod error map — the SSOT that rewrites Zod's opaque default
 * issue text ("Too small: expected array to have >=2 items", "Invalid
 * input") into clear, s-expr-oriented messages naming the arity / shape
 * contract the caller actually violated. Installed *parse-level* at every
 * op-call validation via {@link parseOpCall}; Zod-v4 precedence keeps a
 * schema-level `{ error }` (e.g. `col`'s domain hint) winning over this
 * map, so per-op messages stay authoritative. Returns `undefined` for
 * codes whose default is already clear (`invalid_type`, enum / literal
 * `invalid_value`) — Zod then keeps that default; this map only rewrites
 * the genuinely confusing ones (array / string arity, bare `invalid_union`,
 * unrecognized keyword args).
 */
export const engineZodErrorMap: z.core.$ZodErrorMap = (issue) => {
  const i = issue as unknown as ZodRawIssueView;
  const path = i.path ?? [];
  // A failure on the call's positional list itself reads as "argument";
  // anything deeper (a vector element, a kwarg vector) reads as "element".
  const noun = path[0] === 'params' && path.length === 1 ? 'argument' : 'element';
  const count = (n: number, word: string): string => `${word}${n === 1 ? '' : 's'}`;
  const gotArray = Array.isArray(i.input) ? `, got ${i.input.length}` : '';
  // `.min()` / `.max()` ⇒ inclusive bound; `.gt()` / `.lt()` ⇒ exclusive.
  const isInclusive = i.inclusive !== false;

  if (i.code === 'too_small') {
    const min = Number(i.minimum ?? 0);
    if (i.origin === 'array') {
      return `expected ${isInclusive ? `at least ${min}` : `more than ${min}`} ${count(min, noun)}${gotArray}`;
    }
    if (i.origin === 'string') {
      return min <= 1 ? 'must not be empty' : `must be at least ${min} characters`;
    }
    return undefined;
  }

  if (i.code === 'too_big') {
    const max = Number(i.maximum ?? 0);
    if (i.origin === 'array') {
      return `expected ${isInclusive ? `at most ${max}` : `fewer than ${max}`} ${count(max, noun)}${gotArray}`;
    }
    if (i.origin === 'string') {
      return `must be at most ${max} characters`;
    }
    return undefined;
  }

  if (i.code === 'unrecognized_keys') {
    const keys = i.keys ?? [];
    return `unknown keyword ${count(keys.length, 'argument')}: ${keys.map((k) => `:${k}`).join(', ')}`;
  }

  if (i.code === 'invalid_union') {
    const options = [
      ...new Set(
        (i.errors ?? []).flat().flatMap((leaf): readonly string[] => {
          if (Array.isArray(leaf.values)) {
            return leaf.values.map((v) => JSON.stringify(v));
          }
          if (leaf.expected !== undefined) {
            return [leaf.expected];
          }
          return [];
        }),
      ),
    ];
    return options.length > 0 ? `expected one of: ${options.join(' | ')}` : undefined;
  }

  return undefined;
};

/**
 * Validate a normalised op-call shape against its registered Zod schema
 * with {@link engineZodErrorMap} installed. SSOT for op-call validation:
 * every dispatch site (eager + special op calls in `evaluator.ts`,
 * dashboard tile / sub-form dispatch in `ops/dashboard/dispatch.ts`)
 * routes through here, so the clearer messages can never be forgotten at
 * a new call site.
 *
 * {@link relaxSymbols} is applied here (memoized per schema) so every
 * string/enum kwarg accepts a bare s-expr symbol in addition to the
 * quoted form — uniformly, for special ops, dashboard tiles/sub-forms
 * AND eager ops, with no per-op opt-in. The eager path's args are
 * pre-resolved before this point, so the widened symbol arm is simply
 * inert there; structural `{$sym}`/`z.unknown()` slots are not
 * string/enum leaves and are left untouched.
 */
export const parseOpCall = <SchemaType extends z.ZodType>(schema: SchemaType, data: unknown) =>
  (relaxSymbols(schema) as SchemaType).safeParse(data, { error: engineZodErrorMap });

/**
 * A schema validation failure during op-call dispatch. Wraps one Zod
 * issue at a time — multiple issues from a single Zod parse become
 * multiple `ShapeError` instances, aggregated via `AggregateExprError`.
 *
 * `argRef` names the offending argument when the issue is attributable to
 * one (`":span"` for a keyword arg, `"#0"` for a positional); `undefined`
 * means the issue concerns the call as a whole. When set, the rendered
 * message is prefixed with `argument <argRef>: ` and `suggestions()`
 * returns a recovery hint naming that argument — the guidance an LLM
 * agent needs to locate and fix the offending arg.
 */
export class ShapeError extends EvaluationError {
  constructor(
    readonly issueMessage: string,
    pos: SourcePos,
    readonly argRef?: string,
  ) {
    super(argRef === undefined ? issueMessage : `argument ${argRef}: ${issueMessage}`, pos);
  }

  override suggestions(): readonly string[] {
    if (this.argRef === undefined) {
      return [];
    }
    return this.argRef.startsWith(':')
      ? [`Check the value passed to the ${this.argRef} keyword argument against the op's signature.`]
      : [`Check positional argument ${this.argRef} against the op's signature.`];
  }
}

/**
 * Explicit type-mismatch from an op's apply function (e.g. `(dataset)`
 * rejecting a non-Dataset RHS). Distinct from `ShapeError` (which wraps
 * pre-apply schema validation) and `OpExecutionError` (which wraps
 * unexpected throws from apply).
 */
export class TypeMismatchError extends EvaluationError {
  constructor(
    readonly actualKind: string,
    readonly expectedKind: string,
    readonly context: string,
    pos: SourcePos,
  ) {
    super(`${context} requires ${expectedKind}; got ${actualKind}`, pos);
  }
}
/**
 * Thrown when expression-context code (a `filter` predicate, a `derive`
 * right-hand expression, etc.) walks into a column cell that is itself
 * a `Dataset`. Phase 4-prep widens cells to `Scalar | Dataset`, but the
 * existing scalar-only verbs `filter` / `derive` have no per-cell
 * dispatch yet (that lands in 4-prep.5). Until then, references into
 * Dataset-cell columns surface as this error so the failure mode is
 * explicit instead of producing nonsensical results inside arquero's
 * row functions.
 *
 * Carries the offending column name (`column`) and the source position
 * of the column reference inside the expression.
 */
export class NestedDatasetCellError extends EvaluationError {
  constructor(
    readonly column: string,
    pos: SourcePos,
  ) {
    super(
      `column \`${column}\` holds a Dataset cell; expression-context verbs (filter, derive) do not yet support per-cell dispatch on nested Datasets`,
      pos,
    );
  }
}

export class RebindingError extends EvaluationError {
  protected override readonly staticHint = 'Rename the binding or omit the duplicate declaration.';
  constructor(
    override readonly name: string,
    pos: SourcePos,
  ) {
    super(`cannot rebind '${name}': name is already bound`, pos);
  }
}
/**
 * Conversation-scoped rebinding failure. Distinct from the lexical
 * `RebindingError` (which fires inside a single `evalScript` call via
 * `checkBindable`): this one fires when a tool / the `dataset` op tries
 * to bind a name that is *already* a session binding from an earlier
 * conversation turn **to a different expression**. Session dataset
 * bindings are immutable on purpose — a dashboard tile holds an
 * unresolved `(col <name> …)` that resolves at render time via
 * `session.getBinding`, so silently rebinding a name would
 * retroactively change every prior reference. Re-recording the *same*
 * recipe is idempotent (no error) — only a genuine change of meaning
 * is rejected. `instanceof` discrimination keeps it separate from the
 * lexical case.
 */
export class SessionRebindingError extends EvaluationError {
  protected override readonly staticHint: string;
  constructor(
    override readonly name: string,
    /** A concretely-free binding name the caller may use instead. The
     * binding store computes this from the live name set so the agent
     * recovers deterministically instead of blindly suffixing. */
    readonly suggestedName?: string,
    pos: SourcePos = NO_POS,
  ) {
    super(`cannot rebind '${name}': already bound in this conversation`, pos);
    this.staticHint =
      suggestedName === undefined
        ? 'Choose a unique outputDataset name. Conversation dataset bindings are immutable so existing dashboards and downstream references stay stable.'
        : `Conversation dataset bindings are immutable. Use a fresh outputDataset name — e.g. '${suggestedName}' — and reference the existing binding by its name instead of rebinding it.`;
  }
}

export class ShadowingOpError extends EvaluationError {
  protected override readonly staticHint: string;
  constructor(
    override readonly name: string,
    pos: SourcePos,
  ) {
    super(`cannot bind '${name}': name is a registered op`, pos);
    this.staticHint = `Rename the binding; the op '${name}' would be shadowed.`;
  }
}

export class EmptyScriptError extends EvaluationError {
  constructor() {
    super('empty script — no forms to evaluate', NO_POS);
  }
}

/**
 * An op's apply function threw an unexpected (non-`ExprError`) error.
 * The original message is preserved verbatim; callers that need the
 * original throwable can read `underlyingMessage`.
 */
export class OpExecutionError extends EvaluationError {
  constructor(
    override readonly name: string,
    readonly underlyingMessage: string,
    pos: SourcePos,
  ) {
    super(`op '${name}' threw: ${underlyingMessage}`, pos);
  }
}
/**
 * A pipeline-verb call inside `(transform ...)` referenced a name not in
 * the verb registry. Distinct from `UnknownOpError` so the LLM can tell
 * "this name doesn't exist at all" from "this name exists as a top-level
 * op but is not a pipeline verb".
 */
export class UnknownVerbError extends UnknownNameError {
  constructor(name: string, available: readonly string[], pos: SourcePos) {
    super(
      name,
      available,
      pos,
      `unknown pipeline verb: ${name}`,
      'No pipeline verbs are registered.',
      'Valid verbs inside (transform ...)',
    );
  }
}
/**
 * A column-reference in an expression or column-list context named a
 * column not present on the surrounding dataset's `Schema`. Surfaced
 * by the pre-evaluation type-check pass (§5.8) so the LLM gets a
 * spell-check diagnostic without paying for any I/O.
 *
 * Distinct from `UnboundSymbolError` (which is for env-level dataset
 * bindings) so callers can tell "this name isn't a dataset I can bind
 * to" from "this name isn't a column on the dataset I'm transforming."
 */
export class UnknownColumnRefError extends UnknownNameError {
  constructor(name: string, available: readonly string[], pos: SourcePos) {
    super(
      name,
      available,
      pos,
      `unknown column: ${name}`,
      'The input dataset has no columns in scope. Reference a column from the upstream schema.',
      'Available columns on the upstream dataset',
    );
  }
}

/**
 * `compileExpr` was invoked on an op call whose schema has no `compileExpr`
 * method. Surfaces when a non-expression op (e.g. `dataset`, `transform`)
 * appears inside an expression-context argument, like the predicate of a
 * `filter` or the body of a `derive`.
 */
export class NotAnExpressionOpError extends EvaluationError {
  protected override readonly staticHint: string;
  constructor(
    override readonly name: string,
    pos: SourcePos,
  ) {
    super(`op '${name}' cannot be used in an expression context`, pos);
    this.staticHint = `'${name}' has no compileExpr method. Expression-context arguments (filter predicates, derive bodies, sort keys) accept scalar/comparison/boolean ops + column references, not top-level forms.`;
  }
}
/**
 * Thrown by the materializer (sub-task 4-prep.4) when something prevents
 * the lazy-plan execution from completing. Two concrete failure modes
 * today, distinguished by `reason`:
 *
 *   - **`missing-fetcher`** — `EvalContext.sourceFetchers` has no entry
 *     for the plan's source kind (e.g., plan needs `qkg` but `sourceFetchers.qkg`
 *     is undefined). Caller is expected to populate the fetcher set
 *     before invoking the materializer.
 *   - **`schema-mismatch`** — after running the verb chain, the actual
 *     output schema disagrees with the `LazyDataset`'s declared schema.
 *     Indicates a `propagateSchema` bug in one of the verbs; caught at
 *     the materialisation boundary instead of letting downstream
 *     consumers see surprising shapes.
 *
 * The `reason` discriminator is a string tag (not a separate class
 * hierarchy) — both failures share the same shape (message + pos) and
 * the same recovery path (fix the offending verb/config). Tests assert
 * via `instanceof MaterializationError` and inspect `reason` when they
 * care which case fired.
 */
export class MaterializationError extends EvaluationError {
  protected override readonly staticHint: string;
  constructor(
    readonly reason: 'missing-fetcher' | 'schema-mismatch',
    message: string,
    pos: SourcePos,
  ) {
    super(message, pos);
    this.staticHint =
      reason === 'missing-fetcher'
        ? 'Register a fetcher for this source kind on `EvalContext.sourceFetchers` before evaluating expressions that materialise a `LazyDataset`.'
        : "A verb's `propagateSchema` disagrees with its `apply`'s output. Compare the produced columns against the verb's propagation and fix the discrepancy.";
  }
}
/**
 * Thrown when an op's apply tries to use one of the optional
 * `EvalContext` callbacks (`getCogniteClient`, `getViews`) but the
 * caller did not supply it (Phase 4-prep sub-task 4-prep.8). The
 * callbacks are optional on `EvalContext` because non-CDF callers
 * (tests, future CLI, in-memory sessions) don't need them; any op
 * that *does* need them must surface this error so the caller
 * understands what to wire up.
 *
 * The `callbackName` discriminator tells the caller which callback
 * was missing; the `op` field identifies the op that needed it (for
 * the operator-side debugging story).
 */
export class MissingCallbackError extends EvaluationError {
  protected override readonly staticHint: string;
  constructor(
    readonly op: string,
    readonly callbackName: 'getCogniteClient' | 'getViews',
    pos: SourcePos,
  ) {
    super(`op '${op}' requires \`EvalContext.${callbackName}\`, but it was not supplied`, pos);
    this.staticHint = `Construct the \`EvalContext\` with a \`${callbackName}\` callback (via \`useEvalExpr\` or by passing it directly), or avoid invoking '${op}' from this context.`;
  }
}
/**
 * Thrown by `query-knowledge-graph` (and any future view-aware op) when
 * the requested view's `externalId` is not in the host-supplied
 * `EvalContext.getViews()` set. Extends `UnknownNameError` so callers
 * get the standard "available names + did-you-mean" diagnostic shape.
 *
 * Distinct from `UnknownOpError` (op-name lookup), `UnboundSymbolError`
 * (env/session lookup), and `UnknownColumnRefError` (column lookup) so
 * call sites can tell the four "name not in scope" failures apart.
 */
export class UnknownViewError extends UnknownNameError {
  constructor(name: string, available: readonly string[], pos: SourcePos) {
    super(
      name,
      available,
      pos,
      `unknown view: ${name}`,
      'No views are available — pass a `getViews` callback on the `EvalContext` so the engine can resolve view metadata.',
      'Available view externalIds',
    );
  }
}

/**
 * Thrown by `query-knowledge-graph` when the requested `:limit` exceeds
 * the engine's hard cap. The cap is a guardrail against accidental
 * unbounded fetches; surface it as a structured error rather than a
 * silent clamp so the LLM can react.
 *
 * Carries the requested value AND the cap so the caller knows the
 * legal range; not a discriminator-style error (the failure has a
 * single shape).
 */
export class LimitTooLargeError extends EvaluationError {
  protected override readonly staticHint: string;
  constructor(
    readonly limit: number,
    readonly max: number,
    pos: SourcePos,
  ) {
    super(`:limit ${limit} exceeds the engine cap of ${max}`, pos);
    this.staticHint = `Pass \`:limit\` ≤ ${max}, or page the query — the engine does not silently clamp oversized limits.`;
  }
}
/**
 * Thrown by the shared set-reference resolver when a `:where` predicate
 * resolves a `(col other-ds idCol)` cross-dataset reference whose
 * materialised value set exceeds the CDF-safe cap (`SET_REF_MAX`). The
 * values would be pushed to CDF as an `in` filter; CDF rejects oversized
 * lists, so fail fast with an actionable hint rather than surfacing an
 * opaque server rejection seconds later.
 *
 * Carries the resolved set size AND the cap so the caller knows by how
 * much to narrow the upstream query; single-shape failure (not a
 * discriminator-style error).
 */
export class SetRefTooLargeError extends EvaluationError {
  protected override readonly staticHint: string;
  constructor(
    readonly count: number,
    readonly max: number,
    pos: SourcePos,
  ) {
    super(`cross-dataset \`in\`-set resolved to ${count} values, exceeding the cap of ${max}`, pos);
    this.staticHint = `Narrow the upstream query so the referenced column yields ≤ ${max} distinct values (e.g. add a tighter \`:where\` or \`:limit\` to the dataset the \`(col …)\` points at).`;
  }
}

/**
 * Thrown by the shared set-reference resolver when a `(col other-ds …)`
 * cross-dataset reference inside a `:where` / `filter` predicate resolves
 * to ZERO distinct values. A pushed-down `in` filter with an empty value
 * list silently matches nothing, which almost always reflects an
 * upstream query that returned no rows (a mistake the LLM should see and
 * correct) rather than an intentional "match nothing" — surface it as a
 * hard, actionable error instead of an empty result set.
 */
export class EmptySetRefError extends EvaluationError {
  protected override readonly staticHint =
    'The referenced dataset produced no rows, so the cross-dataset filter would match nothing. Check that the upstream query (the dataset the `(col …)` points at) actually returns data, or widen its `:where` / `:limit`.';
  constructor(pos: SourcePos) {
    super('cross-dataset set reference resolved to an empty set (the referenced column yielded zero values)', pos);
  }
}

/**
 * Thrown by `(sort …)` when a positional arg names a column whose
 * `ColumnType` doesn't have a natural order — `struct`, `nested-dataset`,
 * `unknown`. Carries the offending column name + its rendered type so
 * the LLM can correct course (typically: pick a different column, or
 * project to a scalar via `derive` first).
 */
export class UnsortableColumnTypeError extends EvaluationError {
  protected override readonly staticHint: string;
  constructor(
    readonly column: string,
    readonly columnType: string,
    pos: SourcePos,
  ) {
    super(`sort: column \`${column}\` has type \`${columnType}\`, which has no natural order`, pos);
    this.staticHint =
      'Sort only supports scalar column types (number / string / boolean / date / timestamp). ' +
      'For struct or nested-dataset columns, project to a scalar with `derive` first.';
  }
}

/**
 * Thrown by `(unnest …)` when a positional column name resolves to a
 * `ColumnType` other than `nested-dataset`. Mirrors
 * `UnsortableColumnTypeError`'s shape — the failure is a single
 * `(col, rendered-type)` pair, not a discriminator family.
 */
export class UnnestableColumnTypeError extends EvaluationError {
  protected override readonly staticHint: string;
  constructor(
    readonly column: string,
    readonly columnType: string,
    pos: SourcePos,
  ) {
    super(`unnest: column \`${column}\` has type \`${columnType}\`, which is not a nested-dataset column`, pos);
    this.staticHint =
      '`unnest` operates on columns whose cells are Datasets (typically timeseries refs). ' +
      'Verify the column is a TS-ref column or another nested-dataset producer.';
  }
}

/**
 * Thrown by `(unnest …)` when an inner column name collides with an
 * outer column name and the LLM has not supplied a `:as` rename for
 * the colliding inner column. Carries both names so the LLM can pick
 * a fresh alias.
 */
export class UnnestCollisionError extends EvaluationError {
  protected override readonly staticHint: string;
  constructor(
    readonly column: string,
    readonly collidingInnerName: string,
    pos: SourcePos,
  ) {
    super(
      `unnest: inner column \`${collidingInnerName}\` (from \`${column}\`) collides with an existing outer column`,
      pos,
    );
    this.staticHint = `Use \`:as\` to rename the inner column — for example \`(unnest [${column}] :as [{${collidingInnerName} new-name}])\`.`;
  }
}
/**
 * Thrown at dashboard render time (4c.7) when a `(series …)` `:x`
 * column's leaf type is incompatible with the parent `(cartesian …)`'s
 * declared `:x-kind`. The mismatch can only be detected once the
 * referenced dataset is materialised (the V-shape carries an
 * unresolved `ColumnRef`), so this is a resolution-time leaf, not an
 * eval-time one. Mirrors `UnsortableColumnTypeError`'s `(col, type)`
 * shape, extended with the expected/actual pair + the x-kind context.
 */
export class SeriesXKindMismatchError extends EvaluationError {
  protected override readonly staticHint: string;
  constructor(
    readonly seriesId: string,
    readonly xKind: string,
    readonly expectedLeafType: string,
    readonly actualLeafType: string,
    pos: SourcePos,
  ) {
    super(
      `series \`${seriesId}\`: :x column resolves to \`${actualLeafType}\`, incompatible with cartesian :x-kind \`${xKind}\` (expected \`${expectedLeafType}\`)`,
      pos,
    );
    this.staticHint = `Reference an :x column whose type matches :x-kind \`${xKind}\` (${expectedLeafType}), or change the cartesian's :x-kind.`;
  }
}

/**
 * Thrown at dashboard render time when a `(reference-line …)` /
 * `(reference-area …)` scalar attribute (`:value` / `:start` / `:end`)
 * has the wrong JS type for the axis it anchors to (e.g. a numeric
 * value on a `time` x-axis). Single failure shape; `xKind` is carried
 * when the offending axis is the cartesian's x-axis (it drives the
 * expected type) and omitted for the y-axis.
 */
export class RefValueTypeMismatchError extends EvaluationError {
  protected override readonly staticHint: string;
  constructor(
    readonly attr: string,
    readonly axis: string,
    readonly expectedJsType: string,
    readonly actualJsType: string,
    pos: SourcePos,
    readonly xKind?: string,
  ) {
    super(
      `reference \`:${attr}\` on the \`${axis}\` axis is \`${actualJsType}\`, expected \`${expectedJsType}\`${
        xKind === undefined ? '' : ` for :x-kind \`${xKind}\``
      }`,
      pos,
    );
    this.staticHint = `Provide a \`${expectedJsType}\` for \`:${attr}\` on the \`${axis}\` axis.`;
  }
}

/**
 * Thrown at dashboard render time when a `ColumnRef`'s struct `path`
 * walks into a scalar (non-struct, non-nested) value mid-path. The
 * col op validates the path against the schema at construction, but a
 * lazy/materialised divergence (or a `unknown`-typed column that turns
 * out scalar) surfaces here. Mirrors `UnsortableColumnTypeError`'s
 * `(name, type)` pair shape plus the offending path segment.
 */
export class PathThroughScalarError extends EvaluationError {
  protected override readonly staticHint: string;
  constructor(
    readonly column: string,
    readonly scalarType: string,
    readonly atSegment: string,
    pos: SourcePos,
  ) {
    super(
      `column \`${column}\`: struct path segment \`${atSegment}\` walks into a \`${scalarType}\` value, which is not a struct`,
      pos,
    );
    this.staticHint = `Drop the path segments after \`${atSegment}\`, or reference a struct / nested-dataset column instead.`;
  }
}
